import {
    Selection,
    DefaultButton,
    IContextualMenuItem,
    IObjectWithKey,
    memoizeFunction,
    TooltipHost,
    TooltipDelay
} from "office-ui-fabric-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import { ApplicationState } from "../../store";
import { IWithFilter, WithFilterProps, withFilter } from "../common/withFilter";
import {
    Dictionary,
    EntityType,
    IExtensibleEntity,
    ITimeframe,
    Quantization,
    SortDirection,
    UserPreferencesSettingsUpdate
} from "../../entities/common";
import { FilterHelper as ProjectFilterHelper } from "../../store/project/filters";
import * as ProjectsListStore from "../../store/ProjectsListStore";
import * as Metadata from "../../entities/Metadata";
import * as FiltersStore from "../../store/filters";
import * as PortfoliosListStore from "../../store/PortfoliosListStore";
import * as ProgramsListStore from "../../store/ProgramsListStore";
import { defaultCatch, mergeDefault } from "../../store/utils";
import { useColumnResize, useScale, useSorting } from "../../store/services/viewSaver";
import { nameof } from "../../store/services/metadataService";
import * as ResourcesListStore from "../../store/ResourcesListStore";
import * as ReportedTimeStore from "../../store/ReportedTimeStore";
import { CommonOperations, ResourceOperations, contains } from "../../store/permissions";
import { bindActionCreators } from "redux";
import EntityTimelineList from "../common/extensibleEntity/EntityTimelineList";
import Spinner, { ControlSpiner } from "../common/Spinner";
import { ScaleRenderMode } from "../common/timeline/TimelineBody";
import { cancellablePost, post } from "../../fetch-interceptor";
import { formatValue, notUndefined, toDate, toDateTime, toDictionaryByName } from "../utils/common";
import { IRow } from "../common/timeline/TimelineList";
import { ITimelineSegment } from "../common/timeline/TimelineSegment";
import { ViewService } from "../../services/ViewService";
import ReportedTimeGridMenu from "./ReportedTime/ReportedTimeGridMenu";
import ReportedTimeCell from "./ReportedTime/ReportedTimeCell";
import "../../components/timeTracking/timeTrackingGrid.css";
import TimeTrackingAdministrativeCategoryEntityName from "../timeTracking/TimeTrackingAdministrativeCategoryEntityName";
import AddEntitiesDialog from "../common/sectionsControl/uiControls/projectsControl/AddEntitiesDialog";
import PersonPickerInput, { PersonInfo } from "../common/inputs/PersonPickerInput";
import { getWorkingHoursBetweenDates } from "../common/timeline/utils";
import ExportReportedTimePanel from "./ReportedTime/ExportReportedTimePanel";
import ReportedTimePeriodNavigation from "./ReportedTime/ReportedTimePeriodNavigation";
import "./ReportedTime/reportedTimeList.css";
import { ViewTypeSelect } from "../common/ViewTypeSelect";
import ReportedTimeApprovalTooltip from "./ReportedTime/ReportedTimeApprovalTooltip";
import { getFileTypeIconProps } from "@uifabric/file-type-icons";
import {
    TimeReportingPeriod,
    findPeriodForDate,
    actionCreators as timeReportingPeriodsActionCreators
} from "../../store/TimeReportingPeriodsStore";
import { IsTimesheetApprovalsEnabled, ApprovalStatusField, renderApprovalStatusIcon } from "../timeTracking/common";
import { TimesheetLineStatus } from "../../store/TimeTrackingStore";
import { getFirstDayOfWeekSetting } from "../../store/selectors/settingsSelectors";
import { Calendar } from "../../store/calendar/common";

type OwnProps = {
    resourceIds: string[];
};

type StoreProps = {
    resourceFields: Metadata.Field[];
    resourceFakeFields: Metadata.Field[];
    projectFields: Metadata.Field[];
    entityLayouts: Metadata.Layout[];
    isFiltersDisabled: boolean;
    filters: Metadata.IFilter<Metadata.BaseFilterValue>[];
    canManageConfiguration: boolean;
    portfolios: PortfoliosListStore.Portfolio[];
    programs: ProgramsListStore.Program[];

    controlSetting: ControlSettings;
    isLoading: boolean;
    resourcesMap: Dictionary<ResourcesListStore.Resource>;
    calendarsById: Dictionary<Calendar>;
    periods: TimeReportingPeriod[];
    isTimesheetApprovalsEnabled: boolean;
    firstDayOfWeek: number;
};

type ActionProps = {
    filtersActions: ReturnType<typeof FiltersStore.actionCreators.forEntity>;
    reportedTimeActions: typeof ReportedTimeStore.actionCreators;
    portfolioActions: typeof PortfoliosListStore.actionCreators;
    programActions: typeof ProgramsListStore.actionCreators;
    timeReportingPeriodsActions: typeof timeReportingPeriodsActionCreators;
};

type InnerProps = OwnProps &
    StoreProps &
    ActionProps &
    RouteComponentProps<{}> & {
        projects: ReportedTimeProjectOrAdministrativeCategory[];
        reportedTime: ReportedTimeStore.ResourcesReportedTimeMap;
        projectsResourcesMap: ReportedTimeStore.ReportedProjectsResourcesMap;
        timesheetsByResource: Dictionary<ReportedTimeStore.TimesheetLineApproval[]>;
        selectedPeriod: TimeReportingPeriod | undefined;
        isEntitiesLoading: boolean;
        controlSettings: ControlSettings;
        onSaveSettings?: (update: UserPreferencesSettingsUpdate) => void;
        loadEntities: (resourceIds: string[]) => void;
        onTimeFrameChange: (timeFrame: Partial<ITimeframe> | undefined) => void;
    } & IWithFilter<ReportedTimeProjectOrAdministrativeCategory>;

type WithEntityType = {
    entityType: EntityType;
};

type ApprovalProjectInfo = {
    id: string;
    name: string;
    imageId?: string;
    isAdministrativeCategory?: boolean;
};

type WithReportedTime = {
    reportedTime: ReportedTimeStore.ReportedTimeByDay;
    timesheetLine?: ReportedTimeStore.TimesheetLineApproval;
    approvalProject?: ApprovalProjectInfo;
};

type ResourceOrProject = ResourcesListStore.Resource | ReportedTimeProjectOrAdministrativeCategory;

const _initialTimeFrame: ITimeframe = new Date().getThisMonth();

const _totalReportedFieldName = "TotalReported";

type ControlSettings = ReportedTimeStore.ReportedTimeSettings;

const defaultControlSettings: ControlSettings = {
    displayFields: [nameof<ResourcesListStore.ResourceAttrs>("Name"), _totalReportedFieldName],
    grouping: "Resource",
    quantization: Quantization.days,
    sortBy: {
        fieldName: "Name",
        direction: SortDirection.ASC
    },
    timeframe: _initialTimeFrame
};

export const csvImportExportIconProps = {
    iconName: getFileTypeIconProps({ extension: "csv", size: 16, imageFileType: "svg" }).iconName
};

const ReportedTimeList = (props: InnerProps) => {
    const {
        projects,
        controlSettings,
        onSaveSettings,
        filter,
        resourceFields,
        resourceFakeFields,
        projectFields,
        calendarsById,
        isEntitiesLoading,
        resourcesMap,
        resourceIds,
        isLoading,
        reportedTime,
        projectsResourcesMap,
        timesheetsByResource,
        selectedPeriod,
        history,
        loadEntities
    } = props;

    const [isAddingResource, setIsAddingResource] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const viewMode = controlSettings.viewMode ?? "Calendar";
    const showApprovalColumn =
        viewMode === "ReportingPeriod" && props.isTimesheetApprovalsEnabled && !!selectedPeriod?.attributes.ApprovalRequired;

    const allResourceFields = useMemo(() => [...resourceFields, ...resourceFakeFields], [resourceFields, resourceFakeFields]);
    const isFieldFake = useCallback(
        (field: Metadata.Field) => !!field.isFake || resourceFakeFields.some(_ => _.id === field.id),
        [resourceFakeFields]
    );

    const menuFields = useMemo(() => [...allResourceFields, _totalReportedField], [allResourceFields]);

    const fieldsWithApproval = useMemo(
        () => (showApprovalColumn ? [...menuFields, ApprovalStatusField] : menuFields),
        [showApprovalColumn, menuFields]
    );

    const displayFieldsWithApproval = useMemo(
        () =>
            showApprovalColumn
                ? [...controlSettings.displayFields.filter(f => f !== ApprovalStatusField.name), ApprovalStatusField.name]
                : controlSettings.displayFields.filter(f => f !== ApprovalStatusField.name),
        [showApprovalColumn, controlSettings.displayFields]
    );

    const filteredProjects = useMemo(
        () => projects.filter(_ => filter.isItemVisible(_) && projectsResourcesMap[_.id]),
        [projects, projectsResourcesMap, filter]
    );

    const resources = useMemo(() => resourceIds.map(_ => resourcesMap[_]), [resourceIds, resourcesMap]);

    const nameField = useMemo(() => resourceFields.find(_ => _.name === "Name"), [resourceFields]);

    const saverProps = {
        ...useColumnResize(controlSettings, onSaveSettings),
        ...useScale(controlSettings, onSaveSettings),
        sorting: useSorting(controlSettings, onSaveSettings)
    };

    const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);

    const rowSelection = useMemo(
        () =>
            new Selection<IObjectWithKey>({
                onSelectionChanged: () => {
                    setSelectedEntityIds(rowSelection.getSelection().map(_ => (_ as ResourceOrProject).id));
                },
                canSelectItem: item => {
                    const data = item as WithEntityType;
                    const groupBy = controlSettings.grouping === "Project" ? EntityType.Project : EntityType.Resource;

                    return data.entityType === groupBy;
                },
                getKey: item => (item as ResourceOrProject).id
            }),
        [controlSettings.grouping]
    );

    const entities: IExtensibleEntity[] = useMemo(
        () => (controlSettings.grouping === "Project" ? filteredProjects : resources),
        [controlSettings.grouping, filteredProjects, resources]
    );

    const buildRow = useBuildRow(
        controlSettings.grouping,
        resourcesMap,
        reportedTime,
        projectsResourcesMap,
        timesheetsByResource,
        filteredProjects
    );
    const renderSegmentContent = useRenderSegmentContent(controlSettings.grouping, calendarsById);
    const onItemRender = useOnItemRender(projectFields, allResourceFields, nameField);

    return (
        <div className="resource-reported-time">
            <div className="page-navigation">
                <DefaultButton
                    text="Back to Resources"
                    iconProps={{ iconName: "Back" }}
                    href="/resources"
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        history.replace("/resources");
                    }}
                />
                <div className="align-right">
                    <ViewTypeSelect
                        views={[
                            { type: "Calendar" as ReportedTimeStore.ViewMode, title: "Calendar view", iconName: "Calendar" },
                            {
                                type: "ReportingPeriod" as ReportedTimeStore.ViewMode,
                                title: "Reporting Period view",
                                iconName: "Clock"
                            }
                        ]}
                        selected={viewMode}
                        onItemSelected={mode =>
                            onSaveSettings?.({
                                parentSettingsPathKeys: [],
                                valueBySettingNameMap: { viewMode: mode }
                            })
                        }
                    />
                </div>
            </div>
            <h2>Resource Reported Time</h2>
            <div className="entities-list">
                {!resources.length || isLoading ? (
                    <Spinner />
                ) : (
                    <ControlSpiner isLoading={isEntitiesLoading} className="show-over">
                        <ReportedTimeGridMenu
                            entityType={EntityType.Resource}
                            mandatoryViewFields={ViewService.getViewMandatoryFields(EntityType.Resource)}
                            fields={menuFields}
                            isFieldFake={isFieldFake}
                            type={"Columns"}
                            displayFields={controlSettings.displayFields}
                            canViewConfigureColumns
                            onDisplayFieldsChange={_ =>
                                onSaveSettings?.({
                                    parentSettingsPathKeys: [],
                                    valueBySettingNameMap: {
                                        [nameof<ControlSettings>("displayFields")]: _
                                    }
                                })
                            }
                            commands={[
                                {
                                    key: "addRow",
                                    name: "Add Resource",
                                    iconProps: { iconName: "Add" },
                                    onClick: () => setIsAddingResource(true)
                                },
                                {
                                    key: "exportAll",
                                    name: "Export to CSV",
                                    iconProps: csvImportExportIconProps,
                                    onClick: () => setIsExporting(true)
                                }
                            ]}
                            selectionMode={{
                                items: [
                                    {
                                        key: "exportSelected",
                                        name: "Export to CSV",
                                        iconProps: csvImportExportIconProps,
                                        onClick: () => setIsExporting(true)
                                    }
                                ],
                                enabled: selectedEntityIds.length > 0,
                                onCancel: () => rowSelection.setAllSelected(false)
                            }}
                            farCommands={[
                                {
                                    key: "grouping",
                                    name: `Group by: ${controlSettings.grouping}`,
                                    iconProps: { iconName: "ViewListGroup" },
                                    subMenuProps: {
                                        items: [
                                            "Resource" as ReportedTimeStore.Grouping,
                                            "Project" as ReportedTimeStore.Grouping
                                        ].map<IContextualMenuItem>(_ => ({
                                            key: _,
                                            name: _,
                                            canCheck: true,
                                            checked: controlSettings.grouping === _,
                                            onClick: () => {
                                                if (_ != controlSettings.grouping) {
                                                    rowSelection.setAllSelected(false);

                                                    onSaveSettings?.({
                                                        parentSettingsPathKeys: [],
                                                        valueBySettingNameMap: { [nameof<ControlSettings>("grouping")]: _ }
                                                    });
                                                }
                                            }
                                        }))
                                    }
                                }
                            ]}
                            {...filter.menu}
                        />

                        <div data-is-scrollable="true" className="entities-list-body">
                            <EntityTimelineList
                                key="timeline"
                                fields={fieldsWithApproval}
                                isFieldFake={isFieldFake}
                                displayFields={displayFieldsWithApproval}
                                entities={entities}
                                renderTimelineHeaderFirstRow={
                                    viewMode === "ReportingPeriod"
                                        ? () => (
                                            <ReportedTimePeriodNavigation
                                                periods={props.periods}
                                                selectedPeriodId={controlSettings.selectedPeriodId}
                                                onPeriodChange={period => {
                                                    onSaveSettings?.({
                                                        parentSettingsPathKeys: [],
                                                        valueBySettingNameMap: { selectedPeriodId: period.id }
                                                    });
                                                }}
                                                firstDayOfWeek={props.firstDayOfWeek}
                                            />
                                        )
                                        : undefined
                                }
                                buildTree
                                entityType={EntityType.Resource}
                                scaleMultiplier={2.5} //cell width,
                                renderHeaderCellContent={_ =>
                                    _.key === nameField?.id ? <>Resource / Project</> : undefined
                                }
                                selection={rowSelection}
                                buildRow={buildRow}
                                scaleRenderMode={ScaleRenderMode.Cell}
                                renderSegmentContent={renderSegmentContent}
                                {...saverProps}
                                userQuantization={viewMode === "ReportingPeriod" ? Quantization.days : saverProps.userQuantization}
                                onScaleChange={timelineChange => {
                                    if (viewMode !== "ReportingPeriod" && timelineChange.origin) {
                                        props.onTimeFrameChange(timelineChange.origin.timeframe);
                                    }

                                    saverProps.onScaleChange(timelineChange);
                                }}
                                onItemRender={onItemRender}
                            />
                        </div>
                    </ControlSpiner>
                )}

                {isAddingResource && (
                    <AddEntitiesDialog
                        confirmButtonText="Add Resources"
                        dialogContentProps={{
                            title: "Add Resources",
                            subText: "Select resources to view reported time"
                        }}
                        onDismiss={() => setIsAddingResource(false)}
                        onComplete={newResourceIds => {
                            loadEntities(newResourceIds);
                            setIsAddingResource(false);
                        }}
                        renderEntitySelector={onChanged => (
                            <PersonPickerInput
                                multichoice={true}
                                onChanged={value => value && onChanged((value as PersonInfo[]).map(_ => _.id))}
                                searchUrl="api/resource/findAllowed"
                                filter={{ exceptIds: resourceIds }}
                                inputProps={{ placeholder: "Type to search", autoFocus: true }}
                            />
                        )}
                    />
                )}

                {isExporting && (
                    <ExportReportedTimePanel
                        resourceIds={
                            controlSettings.grouping === "Resource" && selectedEntityIds.length
                                ? selectedEntityIds
                                : resourceIds
                        }
                        projectIds={
                            controlSettings.grouping === "Project" && selectedEntityIds.length
                                ? selectedEntityIds
                                : filteredProjects.map(_ => _.id)
                        }
                        timeFrame={{
                            start: toDateTime(controlSettings.timeframe?.start),
                            end: toDateTime(controlSettings.timeframe?.end)
                        }}
                        onDismiss={() => setIsExporting(false)}
                    />
                )}
            </div>
        </div>
    );
};

const useBuildRow = (
    grouping: ReportedTimeStore.Grouping,
    resourcesMap: Dictionary<ResourcesListStore.Resource>,
    reportedTime: ReportedTimeStore.ResourcesReportedTimeMap,
    projectsResourcesMap: ReportedTimeStore.ReportedProjectsResourcesMap,
    timesheetsByResource: Dictionary<ReportedTimeStore.TimesheetLineApproval[]>,
    filteredProjects: ReportedTimeProjectOrAdministrativeCategory[]
) => {
    return useCallback(
        (groupingEntity: IExtensibleEntity) => {
            if (grouping === "Project") {
                return _buildProjectGroup(
                    groupingEntity,
                    resourcesMap,
                    reportedTime,
                    projectsResourcesMap,
                    timesheetsByResource
                );
            }

            return _buildResourceGroups(groupingEntity, reportedTime, filteredProjects, timesheetsByResource);
        },
        [grouping, resourcesMap, reportedTime, projectsResourcesMap, timesheetsByResource, filteredProjects]
    );
};

const useRenderSegmentContent = (grouping: ReportedTimeStore.Grouping, calendarsById: Dictionary<Calendar>) => {
    return useCallback(
        (row: IRow, segment: ITimelineSegment) => {
            const entity = row.entity as any as WithEntityType & WithReportedTime;

            const isResourceTotalRow = grouping === "Resource" && entity.entityType === EntityType.Resource;

            const isTotal = isResourceTotalRow || (grouping === "Project" && entity.entityType === EntityType.Project);

            let expected: number | undefined;

            if (isResourceTotalRow) {
                const resource = row.entity as ResourcesListStore.Resource;

                expected = _getWorkingHoursBetweenDates(resource, calendarsById, segment.startDate, segment.finishDate);
            }

            return (
                <ReportedTimeCell
                    reportedTime={entity.reportedTime}
                    segment={segment}
                    isTotal={isTotal}
                    expected={expected}
                />
            );
        },
        [grouping, calendarsById]
    );
};

const useOnItemRender = (
    projectFields: Metadata.Field[],
    resourceFields: Metadata.Field[],
    nameField: Metadata.Field | undefined
) => {
    const projectFieldsByName = useMemo(() => toDictionaryByName(projectFields), [projectFields]);
    const resourceFieldsByName = useMemo(() => toDictionaryByName(resourceFields), [resourceFields]);

    return useCallback(
        (
            entity: WithEntityType & WithReportedTime & ReportedTimeProjectOrAdministrativeCategory,
            _: any,
            field: Metadata.Field,
            defaultRender: any
        ) => {
            if (field.name === ApprovalStatusField.name) {
                if (!entity.timesheetLine || !entity.approvalProject) {
                    return <div className="approval-status"></div>;
                }
                return (
                    <div className="approval-status">
                        <TooltipHost
                            tooltipProps={{
                                onRenderContent: () => (
                                    <ReportedTimeApprovalTooltip
                                        timesheetLine={entity.timesheetLine!}
                                        project={entity.approvalProject!}
                                    />
                                ),
                                maxWidth: "450px"
                            }}
                            delay={TooltipDelay.long}>
                            {renderApprovalStatusIcon(entity.timesheetLine.status)}
                        </TooltipHost>
                    </div>
                );
            }

            if (field.id === _totalReportedField.id) {
                const totalReported = (field as Metadata.CalculatedField).calculate(entity);
                return <div className="total-reported">{formatValue(totalReported, Metadata.FormatType.Duration)}</div>;
            }

            if (entity.entityType === EntityType.Project) {
                if (entity.isAdministrativeCategory && field.name === nameField?.name) {
                    return <TimeTrackingAdministrativeCategoryEntityName categoryId={entity.id} />;
                }

                const entityColumn = ViewService.buildColumn(
                    field.name,
                    projectFieldsByName,
                    undefined,
                    true,
                    EntityType.Project
                );
                return entityColumn ? entityColumn.onRender?.(entity, undefined, entityColumn) : null;
            }

            const resourceColumn = ViewService.buildColumn(
                field.name,
                resourceFieldsByName,
                undefined,
                true,
                EntityType.Resource
            );
            return resourceColumn ? resourceColumn.onRender?.(entity, undefined, resourceColumn) : null;
        },
        [projectFieldsByName, resourceFieldsByName, nameField]
    );
};

const _getWorkingHoursBetweenDates = (
    resource: ResourcesListStore.Resource,
    calendarsById: Dictionary<Calendar>,
    start: Date,
    end: Date
) => {
    let resourceCalendar = resource.usage?.calendar;
    if (resourceCalendar && !resourceCalendar.exceptions.length && !resourceCalendar.workDayExpectedHrs) {
        resourceCalendar = undefined;
    }

    return getWorkingHoursBetweenDates(start, end, _memoizedCreateResourceCalendar(resourceCalendar, calendarsById[resource.calendar.id]));
};

const _memoizedCreateResourceCalendar = memoizeFunction(
    (resourceCalendar: ResourcesListStore.ResourceCalendar | undefined, baseCalendar: Calendar) => {
        return ResourcesListStore.calculateResourceCalendar(resourceCalendar, baseCalendar);
    }
);

const _totalReportedField: Metadata.CalculatedField = {
    id: "totalReported",
    name: _totalReportedFieldName,
    type: Metadata.FieldType.Text,
    isNative: false,
    isSystem: false,
    isCustom: false,
    group: Metadata.FieldGroup.SystemFields,
    label: "Total Reported",
    isReadonly: true,
    isFake: true,
    calculate: (item: WithReportedTime) => {
        return Object.keys(item.reportedTime).reduce((sum, day) => sum + (item.reportedTime[day] ?? 0), 0);
    }
};

const _draftTimesheetLine: ReportedTimeStore.TimesheetLineApproval = {
    projectId: null,
    isAdministrative: false,
    status: TimesheetLineStatus.Draft,
    approvers: null,
    decision: null
};

const _buildResourceGroups = (
    groupingEntity: IExtensibleEntity,
    reportedTime: ReportedTimeStore.ResourcesReportedTimeMap,
    filteredProjects: ReportedTimeProjectOrAdministrativeCategory[],
    timesheetsByResource: Dictionary<ReportedTimeStore.TimesheetLineApproval[]>
) => {
    const resourceId = groupingEntity.id;
    const resourceReportedTime = reportedTime[resourceId]?.projects || {};
    const projectsWithReportedTime = filteredProjects.filter(project => resourceReportedTime[project.id]);
    const timesheetLines = timesheetsByResource[resourceId];

    const subItems: IRow[] = projectsWithReportedTime.map(project => {
        return {
            key: `${groupingEntity.id}_${project.id}`,
            entity: {
                ...project,
                entityType: EntityType.Project,
                reportedTime: resourceReportedTime[project.id].reportedTime,
                parentId: groupingEntity.id,
                timesheetLine:
                    timesheetLines?.find(l =>
                        project.isAdministrativeCategory ? l.isAdministrative : l.projectId === project.id
                    ) ?? _draftTimesheetLine,
                approvalProject: _toApprovalProject(project)
            },
            segments: [],
            markers: []
        };
    });

    return {
        key: groupingEntity.id,
        entity: {
            ...groupingEntity,
            entityType: EntityType.Resource,
            reportedTime: _mergeReportedTime(
                projectsWithReportedTime.map(project => resourceReportedTime[project.id].reportedTime)
            )
        },
        markers: [],
        segments: [],
        subItemType: EntityType.Project,
        subItems: subItems
    };
};

const _buildProjectGroup = (
    groupingEntity: IExtensibleEntity,
    resourceMaps: Dictionary<ResourcesListStore.Resource>,
    reportedTime: ReportedTimeStore.ResourcesReportedTimeMap,
    projectsResourcesMap: ReportedTimeStore.ReportedProjectsResourcesMap,
    timesheetsByResource: Dictionary<ReportedTimeStore.TimesheetLineApproval[]>
) => {
    const projectId = groupingEntity.id;
    const projectResourceIds = projectsResourcesMap[projectId]?.resources || [];

    const projectResources = projectResourceIds.map(_ => resourceMaps[_]);

    const project = groupingEntity as ReportedTimeProjectOrAdministrativeCategory;

    const subItems: IRow[] = projectResources.map(resource => {
        return {
            key: `${groupingEntity.id}_${resource.id}`,
            entity: {
                ...resource,
                entityType: EntityType.Resource,
                reportedTime: reportedTime[resource.id]?.projects[projectId]?.reportedTime ?? {},
                parentId: groupingEntity.id,
                timesheetLine:
                    timesheetsByResource[resource.id]?.find(
                        l => l.projectId === projectId || (l.isAdministrative && !l.projectId)
                    ) ?? _draftTimesheetLine,
                approvalProject: _toApprovalProject(project)
            },
            segments: [],
            markers: []
        };
    });

    return {
        key: groupingEntity.id,
        entity: {
            ...groupingEntity,
            entityType: EntityType.Project,
            reportedTime: _mergeReportedTime(subItems.map(_ => (_.entity as any).reportedTime))
        },
        markers: [],
        segments: [],
        subItemType: EntityType.Resource,
        subItems: subItems
    };
};

type ReportedTimeProjectOrAdministrativeCategory = ProjectsListStore.ProjectInfo & {
    isAdministrativeCategory?: boolean;
};

const _toApprovalProject = (project: ReportedTimeProjectOrAdministrativeCategory): ApprovalProjectInfo => ({
    id: project.id,
    name: project.attributes.Name,
    imageId: project.imageId,
    isAdministrativeCategory: project.isAdministrativeCategory
});

const useFilterProps = ({
    projects,
    projectFields,
    portfolios,
    programs,
    entityLayouts,
    isFiltersDisabled,
    filters,
    filtersActions,
    canManageConfiguration,
    history,
    location,
    controlSettings,
    onSaveSettings
}: InnerProps): WithFilterProps<ProjectsListStore.ProjectInfo> => {
    const projectsInfo = useMemo(() => projects.map(_ => _ as unknown as ProjectsListStore.ProjectInfo), [projects]);
    const filterHelper = useMemo(
        () =>
            new ProjectFilterHelper({
                projectFields,
                layouts: entityLayouts,
                portfolios,
                programs,
                projects: projectsInfo
            }),
        [projectFields, entityLayouts, portfolios, programs, projectsInfo]
    );

    return {
        filterHelper,
        entityType: EntityType.Project,
        disabled: isFiltersDisabled
            ? `Project filter is available for users with "View all" Projects permission.`
            : undefined,
        filters,
        filtersActions,
        canManageConfiguration,
        entities: projectsInfo,
        fields: projectFields,
        history,
        location,
        controlSettings,
        onSaveSettings
    };
};

const WithFilter = withFilter(ReportedTimeList, useFilterProps, FiltersStore.FilterKeys.ReportedTime);

type Props = OwnProps & StoreProps & ActionProps & RouteComponentProps<{}>;

const ReportedTimeListWrapper = (props: Props) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [reportedTime, setReportedTime] = useState<ReportedTimeStore.ResourcesReportedTime>();
    const [timesheetsByResource, setTimesheetsByResource] = useState<Dictionary<ReportedTimeStore.TimesheetLineApproval[]>>(
        {}
    );
    const [timeFrame, setTimeFrame] = useState<Partial<ITimeframe>>();

    const [resourceIds, setResourceIds] = useState(props.resourceIds);

    const [projects, setProjects] = useState<ProjectsListStore.ProjectInfo[]>([]);

    useEffect(() => {
        props.reportedTimeActions.loadSettings();
        props.portfolioActions.requestPortfolios();
        props.programActions.requestPrograms();
        props.timeReportingPeriodsActions.loadPeriods();
    }, []);

    const controlSettings = mergeDefault<ControlSettings>(defaultControlSettings, props.controlSetting);
    const viewMode = controlSettings.viewMode ?? "Calendar";

    const selectedPeriod = useMemo(() => {
        if (viewMode !== "ReportingPeriod" || !props.periods.length) {
            return undefined;
        }
        if (controlSettings.selectedPeriodId) {
            const found = props.periods.find(p => p.id === controlSettings.selectedPeriodId);
            if (found) {
                return found;
            }
        }
        const today = new Date();
        return findPeriodForDate(today, props.periods) ?? props.periods[0];
    }, [viewMode, props.periods, controlSettings.selectedPeriodId]);

    if (viewMode === "ReportingPeriod" && selectedPeriod) {
        controlSettings.timeframe = {
            start: selectedPeriod.attributes.StartDate,
            end: selectedPeriod.attributes.EndDate
        };
    } else {
        controlSettings.timeframe!.start ??= timeFrame?.start ?? _initialTimeFrame.start;
        controlSettings.timeframe!.end ??= timeFrame?.end ?? _initialTimeFrame.end;
    }

    const start = toDateTime(controlSettings.timeframe!.start)!;
    const end = toDateTime(controlSettings.timeframe!.end)!;

    useEffect(() => {
        if (props.isLoading) {
            return;
        }

        setLoading(true);

        if (viewMode === "ReportingPeriod" && selectedPeriod) {
            const request = cancellablePost<ReportedTimeStore.ReportedTimePeriodResponse>(
                `api/reported-time/period/${selectedPeriod.id}`,
                {
                    resourceIds: resourceIds
                }
            );

            request.promise
                .then(data => {
                    _fixDates(data.reportedTime);
                    setReportedTime(data.reportedTime);
                    setTimesheetsByResource(data.timesheetsByResource ?? {});
                    setLoading(false);
                })
                .catch(defaultCatch());

            return () => request.cancelTokenSource.cancel();
        } else {
            const param = {
                start: start.toDateOnlyString(),
                end: end.toDateOnlyString(),
                resourceIds: resourceIds
            };

            const request = cancellablePost<ReportedTimeStore.ResourcesReportedTime>(`api/reported-time`, param);

            request.promise
                .then(data => {
                    _fixDates(data);
                    setReportedTime(data);
                    setTimesheetsByResource({});
                    setLoading(false);
                })
                .catch(defaultCatch());

            return () => request.cancelTokenSource.cancel();
        }
    }, [
        props.isLoading,
        resourceIds.join(""),
        viewMode,
        selectedPeriod?.id,
        start.toDateOnlyString(),
        end.toDateOnlyString()
    ]);

    useEffect(() => {
        if (!reportedTime?.projectsResourcesMap) {
            return;
        }

        const manualProjects: ReportedTimeProjectOrAdministrativeCategory[] = Object.keys(
            reportedTime.projectsResourcesMap
        ).map(projectId => {
            const project = reportedTime.projectsResourcesMap[projectId].project;

            return {
                id: project.id,
                attributes: {
                    Name: project.name
                },
                isAdministrativeCategory: project.isAdministrativeCategory,
                portfolioViaProgram: [],
                sourceInfos: [],
                warnings: []
            } as any;
        });

        const missedManualProjects = manualProjects.filter(_ => !projects.some(p => p.id === _.id));

        if (missedManualProjects.length) {
            let newProjects = [...projects, ...missedManualProjects];

            setProjects(newProjects);

            const projectIdsToLoad = missedManualProjects.filter(_ => !_.isAdministrativeCategory).map(_ => _.id);

            if (projectIdsToLoad.length) {
                post<ProjectsListStore.ProjectInfo[]>(`api/reported-time/projects`, {
                    ids: projectIdsToLoad
                })
                    .then(data => {
                        if (data.length) {
                            newProjects = newProjects.map(_ => data.find(p => p.id === _.id) || _);
                            setProjects(newProjects);
                        }
                    })
                    .catch(defaultCatch());
            }
        }
    }, [reportedTime]);

    const listProps = {
        ...props,
        reportedTime: reportedTime?.resources || {},
        projectsResourcesMap: reportedTime?.projectsResourcesMap || {},
        timesheetsByResource: timesheetsByResource,
        selectedPeriod: selectedPeriod,
        projects: projects,
        isEntitiesLoading: loading,
        controlSettings: controlSettings,
        onSaveSettings: props.isLoading ? undefined : props.reportedTimeActions.saveSettings,
        loadEntities: (_: string[]) => {
            setResourceIds([...resourceIds, ..._]);
        },
        onTimeFrameChange: (newTimeFrame: Partial<ITimeframe> | undefined) => {
            if (!newTimeFrame?.start && !newTimeFrame?.end) {
                setTimeFrame(undefined);
                return;
            }
            if (
                (newTimeFrame?.start && newTimeFrame.start.toDateOnlyString() !== start.toDateOnlyString()) ||
                (newTimeFrame?.end && newTimeFrame.end.toDateOnlyString() !== end.toDateOnlyString())
            ) {
                setLoading(true);
                setTimeFrame(newTimeFrame);
            }
        }
    };

    return <WithFilter {...listProps} resourceIds={resourceIds} />;
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps): StoreProps => {
    const layouts = state.layouts[EntityType.Project];
    const resourceFields = state.fields[EntityType.Resource];
    const projectFields = state.fields[EntityType.Project];

    const filters = FiltersStore.getFilter(state.filters, EntityType.Project);

    const isFiltersDisabled = !contains(state.user.permissions.project.global, ResourceOperations.Read);

    return {
        controlSetting: state.reportedTime.settings,
        isLoading: state.reportedTime.isLoading,
        entityLayouts: layouts.allIds.map(_ => layouts.byId[_]),
        resourceFields: resourceFields.allIds.map(_ => resourceFields.byId[_]),
        resourceFakeFields: [ViewService.findResourceCalendarField(state.views[EntityType.Resource].list.fakeFields)].filter(notUndefined),
        projectFields: projectFields.allIds.map(_ => projectFields.byId[_]),
        filters: isFiltersDisabled ? FiltersStore.getCriteriaFreeFilters(filters.all) : filters.all,
        portfolios: state.portfolios.allIds.map(_ => state.portfolios.byId[_]),
        programs: state.programs.allIds.map(_ => state.programs.byId[_]),
        isFiltersDisabled: isFiltersDisabled,
        canManageConfiguration: contains(state.user.permissions.common, CommonOperations.ConfigurationManage),
        resourcesMap: { ...state.resources.byId },
        calendarsById: state.calendars.byId,
        periods: state.timeReportingPeriods.periods,
        isTimesheetApprovalsEnabled: IsTimesheetApprovalsEnabled(state.tenant),
        firstDayOfWeek: getFirstDayOfWeekSetting(state)
    };
};

const mergeActionCreators = (dispatch: any, ownProps: OwnProps): ActionProps => {
    return {
        filtersActions: bindActionCreators(
            FiltersStore.actionCreators.forEntity(EntityType.Project, FiltersStore.FilterKeys.ReportedTime),
            dispatch
        ),
        reportedTimeActions: bindActionCreators(ReportedTimeStore.actionCreators, dispatch),
        portfolioActions: bindActionCreators(PortfoliosListStore.actionCreators, dispatch),
        programActions: bindActionCreators(ProgramsListStore.actionCreators, dispatch),
        timeReportingPeriodsActions: bindActionCreators(timeReportingPeriodsActionCreators, dispatch)
    };
};

export default withRouter<OwnProps>(connect(mapStateToProps, mergeActionCreators)(ReportedTimeListWrapper));

const _fixDates = (data: ReportedTimeStore.ResourcesReportedTime): void => {
    for (const resourceId of Object.keys(data.resources)) {
        const resourceReportedTime = data.resources[resourceId].projects;

        for (const projectId of Object.keys(resourceReportedTime)) {
            const reportedTimeByDay = resourceReportedTime[projectId].reportedTime;

            for (const date of Object.keys(reportedTimeByDay)) {
                const newKey = toDate(date)!.getBeginOfDay().toISOString();

                if (date !== newKey) {
                    reportedTimeByDay[newKey] = reportedTimeByDay[date];
                    delete reportedTimeByDay[date];
                }
            }
        }
    }
};

const _mergeReportedTime = (reportedTime: ReportedTimeStore.ReportedTimeByDay[]): ReportedTimeStore.ReportedTimeByDay => {
    const result: ReportedTimeStore.ReportedTimeByDay = {};

    for (const entry of reportedTime) {
        for (const day of Object.keys(entry)) {
            result[day] = (result[day] ?? 0) + entry[day];
        }
    }

    return result;
};
